import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BadgeCheck,
  CalendarDays,
  ClipboardList,
  Download,
  ExternalLink,
  IdCard,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  Wallet,
  CircleAlert,
  CircleDashed,
  CircleCheck,
} from "lucide-react";
import { PACKAGES, SLOT_LABELS, baseFor, computeTotal } from "@/lib/mockStore";
import { useAuth } from "@/contexts/AuthContext";
import { paymentApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import AbstractSubmissions from "@/components/AbstractSubmissions";
import AccompanyingPersons from "@/components/AccompanyingPersons";

const LOGO_API =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/1q137q9b_f04cf28b-14e3-4e14-b6c6-e1574fbf1d60.JPG";
const LOGO_ASSAM =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/ka43vom0_9012ac3f-853f-407b-aa81-8f5e96f3f583.JPG";

const escapeHtml = (v = "") =>
  String(v).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c],
  );

const money = (n) =>
  n
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

const STATUS_META = {
  unpaid: {
    label: "Not paid yet",
    tone: "bg-red-50 text-red-700 ring-1 ring-red-200",
    Icon: CircleAlert,
    progress: 25,
  },
  pending: {
    label: "Verification pending",
    tone: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
    Icon: CircleDashed,
    progress: 66,
  },
  verified: {
    label: "Payment verified",
    tone: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    Icon: CircleCheck,
    progress: 100,
  },
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "AP";

const downloadSlip = (user, pkg, total) => {
  const statusLabel =
    { unpaid: "Payment pending", pending: "Verification pending", verified: "Confirmed" }[
      user?.paymentStatus || "unpaid"
    ] || "Payment pending";
  const statusTone = user?.paymentStatus === "verified" ? "#16a34a" : "#b45309";

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>APICON 2026 · Delegate Slip · ${escapeHtml(user?.name || "")}</title>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Manrope:wght@400;500;600;700&display=swap" rel="stylesheet"/>
<style>
  *{ box-sizing:border-box; }
  body{ margin:0; padding:40px 24px; background:#f5f5f5; font-family:'Manrope',system-ui,sans-serif; color:#0a0a0a; }
  .sheet{ max-width:820px; margin:0 auto; background:#fff; border-radius:20px; overflow:hidden; box-shadow:0 20px 60px rgba(11,61,122,.12); }
  .banner{ position:relative; background:linear-gradient(135deg,#0b3d7a,#082c5a); color:#fff; padding:36px 44px; overflow:hidden; }
  .banner:after{ content:''; position:absolute; inset:0; background-image:radial-gradient(rgba(255,255,255,.08) 1.5px, transparent 1.5px); background-size:32px 32px; opacity:.5; }
  .banner-row{ position:relative; display:flex; align-items:center; gap:20px; }
  .logo{ width:76px; height:76px; border-radius:50%; background:#fff; padding:4px; object-fit:contain; box-shadow:0 6px 16px rgba(0,0,0,.15); flex-shrink:0; }
  .brand-eyebrow{ text-transform:uppercase; letter-spacing:.22em; font-size:11px; font-weight:700; color:#ffc107; }
  .brand-title{ font-family:'Playfair Display',serif; font-weight:900; font-size:32px; line-height:1; margin:4px 0 0; }
  .brand-caption{ margin-top:6px; font-size:13px; color:rgba(255,255,255,.75); }
  .divider{ width:1px; height:66px; background:rgba(255,255,255,.2); }
  .banner-strip{ position:relative; margin-top:24px; display:flex; flex-wrap:wrap; gap:18px; font-size:13px; color:rgba(255,255,255,.85); }
  .banner-strip strong{ color:#ffc107; }
  .body{ padding:36px 44px 44px; }
  .kicker{ display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; }
  .doc-label{ font-family:'Playfair Display',serif; font-size:22px; font-weight:700; color:#0b3d7a; }
  .status{ display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:999px; background:${statusTone}22; color:${statusTone}; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.16em; }
  .grid{ display:grid; grid-template-columns:1fr 1fr; gap:14px 24px; }
  .cell{ padding:16px; background:#f7fafc; border-radius:12px; }
  .cell .k{ text-transform:uppercase; font-size:10px; letter-spacing:.2em; color:#64748b; font-weight:700; }
  .cell .v{ margin-top:4px; font-size:16px; color:#0b3d7a; font-weight:600; font-family:'Playfair Display',serif; }
  .cell.wide{ grid-column:1/-1; }
  .cell.mono .v{ font-family:'Courier New',monospace; font-weight:500; font-size:14px; }
  .footer-note{ margin-top:28px; padding:16px 18px; background:#fef3c7; border:1px solid #fcd34d; border-radius:12px; font-size:12px; color:#7c5303; line-height:1.55; }
  .signoff{ margin-top:28px; display:flex; justify-content:space-between; align-items:flex-end; font-size:12px; color:#64748b; }
  .signoff .stamp{ text-align:right; }
  .signoff .stamp .name{ font-family:'Playfair Display',serif; font-size:14px; color:#0b3d7a; font-weight:700; }
  .controls{ max-width:820px; margin:16px auto 0; display:flex; justify-content:flex-end; gap:8px; }
  .btn{ display:inline-flex; align-items:center; gap:6px; padding:10px 18px; border-radius:999px; font-weight:600; font-size:13px; border:0; cursor:pointer; }
  .btn.primary{ background:#0b3d7a; color:#fff; }
  .btn.ghost{ background:#fff; color:#0b3d7a; border:1px solid #e5e7eb; }
  @media print{
    body{ background:#fff; padding:0; }
    .sheet{ box-shadow:none; border-radius:0; }
    .controls{ display:none; }
    @page{ size:A4; margin:0; }
  }
</style>
</head>
<body>
  <div class="sheet">
    <div class="banner">
      <div class="banner-row">
        <img class="logo" src="${LOGO_API}" alt="API"/>
        <img class="logo" src="${LOGO_ASSAM}" alt="API Assam Chapter"/>
        <div class="divider"></div>
        <div>
          <div class="brand-eyebrow">35th Annual Conference · API</div>
          <div class="brand-title">APICON 2026</div>
          <div class="brand-caption">Association of Physicians of India · Assam Chapter</div>
        </div>
      </div>
      <div class="banner-strip">
        <span><strong>18 – 20 December 2026</strong></span>
        <span>Tinsukia Medical College &amp; Hospital, Assam · India</span>
      </div>
    </div>

    <div class="body">
      <div class="kicker">
        <div class="doc-label">Delegate Registration Slip</div>
        <div class="status">${escapeHtml(statusLabel)}</div>
      </div>

      <div class="grid">
        <div class="cell wide"><div class="k">Delegate</div><div class="v">${escapeHtml(user?.name || "—")}</div></div>
        <div class="cell"><div class="k">Email</div><div class="v" style="font-family:Manrope,sans-serif;font-size:14px;font-weight:500;">${escapeHtml(user?.email || "—")}</div></div>
        <div class="cell"><div class="k">Phone</div><div class="v" style="font-family:Manrope,sans-serif;font-size:14px;font-weight:500;">${escapeHtml(user?.phone || "—")}</div></div>
        <div class="cell"><div class="k">Package</div><div class="v">${escapeHtml(pkg?.label || "Not selected")}</div></div>
        <div class="cell"><div class="k">Slot</div><div class="v" style="text-transform:capitalize;">${escapeHtml(SLOT_LABELS[user?.slot] || "—")}</div></div>
        <div class="cell"><div class="k">Amount</div><div class="v">${escapeHtml(total ? money(total) : "—")}</div></div>
        <div class="cell mono"><div class="k">UTR / Reference</div><div class="v">${escapeHtml(user?.txnRef || "—")}</div></div>
        <div class="cell wide mono"><div class="k">Registration ID</div><div class="v">${escapeHtml(user?.id || "—")}</div></div>
      </div>

      <div class="footer-note">
        This is a computer-generated slip. Please present it (printed or on your
        phone) at the registration desk on <strong>18 December 2026</strong>
        along with a valid photo ID. For queries, write to
        <strong>secretariat@apicon2026.in</strong>.
      </div>

      <div class="signoff">
        <div>Issued: ${new Date().toLocaleString()}</div>
        <div class="stamp">
          <div>Organising Secretary</div>
          <div class="name">APICON 2026 · Tinsukia</div>
        </div>
      </div>
    </div>
  </div>

  <div class="controls">
    <button class="btn ghost" onclick="window.close()">Close</button>
    <button class="btn primary" onclick="window.print()">Print / Save as PDF</button>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(
    url,
    "_blank",
    "noopener,noreferrer,width=900,height=1100",
  );
  if (w) w.focus();
  // Revoke the URL after a short delay so the tab has time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const pkg = PACKAGES.find((p) => p.id === user?.packageId);
  const total =
    user?.amount ||
    (pkg
      ? computeTotal(baseFor(pkg, user?.slot || "regular"))
      : null);
  const status = STATUS_META[user?.paymentStatus || "unpaid"];
  const StatusIcon = status.Icon;

  const steps = [
    { label: "Account created", done: true },
    { label: "Phone verified", done: !!user?.phoneVerified },
    { label: "Package selected", done: !!user?.packageId },
    { label: "Payment submitted", done: user?.paymentStatus !== "unpaid" },
    { label: "Payment verified", done: user?.paymentStatus === "verified" },
  ];

  const downloadInvoice = async () => {
    try {
      const blob = await paymentApi.downloadInvoice();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(user?.invoiceNo || "invoice").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the invoice. Try again in a moment.");
    }
  };

  return (
    <div className="relative bg-white" data-testid="page-dashboard">
      {/* Greeting hero */}
      <section className="relative overflow-hidden bg-apicon-navy py-14 md:py-20">
        <div className="pointer-events-none absolute inset-0 opacity-[0.08] heritage-pattern" />
        <div className="pointer-events-none absolute -top-16 -right-16 h-96 w-96 rounded-full bg-apicon-gold/20 blur-3xl" />

        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-6 px-5 md:flex-row md:items-end md:px-8">
          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-2xl font-semibold text-white ring-1 ring-white/20">
              {initials(user?.name)}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-gold">
                Delegate dashboard
              </div>
              <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
                Namaskar, {user?.name?.split(" ")[0] || "Delegate"}.
              </h1>
              <p className="mt-2 max-w-lg text-sm text-white/70">
                Track your registration, payment status and delegate kit for
                APICON 2026.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="rounded-full border-white/25 bg-white/10 text-white hover:bg-white/15 hover:text-white"
              onClick={() => downloadSlip(user, pkg, total)}
              data-testid="dashboard-download-slip"
            >
              <Download className="mr-2 h-4 w-4" /> Download slip
            </Button>
            {user?.invoiceNo && (
              <Button
                variant="outline"
                className="rounded-full border-apicon-gold/60 bg-apicon-gold/20 text-white hover:bg-apicon-gold/30 hover:text-white"
                onClick={downloadInvoice}
                data-testid="dashboard-download-invoice"
              >
                <Download className="mr-2 h-4 w-4" /> GST invoice
              </Button>
            )}
            <Button
              variant="ghost"
              className="rounded-full text-white hover:bg-white/10 hover:text-white"
              onClick={() => {
                logout();
                toast.success("Signed out");
              }}
              data-testid="dashboard-logout"
            >
              Sign out
            </Button>
          </div>
        </div>
      </section>

      <section className="relative py-14 md:py-20">
        <div className="mx-auto grid w-full max-w-7xl gap-6 px-5 md:px-8 lg:grid-cols-12">
          {/* Registration status card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-8 lg:p-8"
            data-testid="dashboard-status-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                  Registration status
                </div>
                <div className="mt-2 font-display text-2xl font-semibold text-apicon-navy">
                  Progress toward your delegate seat
                </div>
              </div>
              <span
                className={cn(
                  "inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em]",
                  status.tone,
                )}
                data-testid="dashboard-status-badge"
              >
                <StatusIcon className="h-3.5 w-3.5" />
                {status.label}
              </span>
            </div>

            <div className="mt-6">
              <Progress
                value={status.progress}
                className="h-2 bg-gray-100 [&>[data-state='complete']]:bg-apicon-green"
                data-testid="dashboard-progress"
              />
              <div className="mt-2 flex justify-between text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">
                <span>Start</span>
                <span>Package</span>
                <span>Payment</span>
                <span>Verified</span>
              </div>
            </div>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="dashboard-steps">
              {steps.map((s) => (
                <li
                  key={s.label}
                  className={cn(
                    "flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm",
                    s.done
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-gray-200 bg-white text-gray-500",
                  )}
                >
                  <BadgeCheck
                    className={cn(
                      "h-4 w-4",
                      s.done ? "text-emerald-600" : "text-gray-300",
                    )}
                  />
                  {s.label}
                </li>
              ))}
            </ul>

            {user?.paymentStatus !== "verified" && (
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-apicon-mist/60 p-5">
                <div className="max-w-md text-sm text-apicon-navy">
                  {user?.paymentStatus === "pending" ? (
                    <>
                      Your UTR{" "}
                      <span className="font-mono text-apicon-navy">
                        {user?.txnRef}
                      </span>{" "}
                      is being verified by the secretariat. Typical turnaround
                      is under 24 hours.
                    </>
                  ) : (
                    <>Pick a package and complete UPI payment to lock your seat.</>
                  )}
                </div>
                <Link to="/payment" data-testid="dashboard-goto-payment">
                  <Button className="rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]">
                    <Wallet className="mr-2 h-4 w-4" />
                    {user?.paymentStatus === "pending"
                      ? "Review payment"
                      : "Continue to payment"}
                  </Button>
                </Link>
              </div>
            )}
          </motion.div>

          {/* Delegate summary */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-4 lg:p-8"
            data-testid="dashboard-profile-card"
          >
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
              Delegate details
            </div>
            <div className="mt-5 space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <IdCard className="mt-0.5 h-4 w-4 text-apicon-gold" />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                    Registration ID
                  </div>
                  <div className="mt-0.5 font-mono text-apicon-navy">
                    {user?.id}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 text-apicon-gold" />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                    Email
                  </div>
                  <div className="mt-0.5 text-apicon-navy">{user?.email}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 text-apicon-gold" />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                    Phone
                  </div>
                  <div className="mt-0.5 text-apicon-navy">{user?.phone}</div>
                  {user?.phoneVerified && (
                    <Badge className="mt-1 rounded-full bg-emerald-50 text-[10px] uppercase tracking-[0.16em] text-emerald-700 hover:bg-emerald-50">
                      Verified
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Package card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-5 lg:p-8"
            data-testid="dashboard-package-card"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                Selected package
              </div>
              <Link
                to="/payment"
                className="text-xs font-medium text-apicon-navy hover:text-apicon-green"
                data-testid="dashboard-edit-package"
              >
                {pkg ? "Change" : "Choose one"} →
              </Link>
            </div>

            {pkg ? (
              <>
                <div className="mt-4 font-display text-2xl font-semibold text-apicon-navy">
                  {pkg.label}
                </div>
                <div className="text-sm text-gray-500">{pkg.description}</div>
                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl bg-apicon-mist/60 p-4">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-apicon-navy/70">
                      Slot
                    </div>
                    <div className="mt-1 font-display text-base font-semibold capitalize text-apicon-navy">
                      {SLOT_LABELS[user?.slot] || "—"}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-apicon-mist/60 p-4">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-apicon-navy/70">
                      Amount
                    </div>
                    <div className="mt-1 font-display text-base font-semibold text-apicon-navy">
                      {money(total)}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-apicon-mist/60 p-4">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-apicon-navy/70">
                      UTR
                    </div>
                    <div className="mt-1 truncate font-mono text-xs text-apicon-navy">
                      {user?.txnRef || "—"}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="mt-4 rounded-2xl border-2 border-dashed border-gray-200 p-6 text-center">
                <ClipboardList className="mx-auto h-8 w-8 text-gray-400" />
                <div className="mt-3 font-display text-lg font-semibold text-apicon-navy">
                  No package selected yet
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Pick a Delegate or PG package to
                  continue.
                </p>
                <Link
                  to="/payment"
                  className="mt-4 inline-block"
                  data-testid="dashboard-pick-package"
                >
                  <Button className="rounded-full bg-apicon-navy font-semibold text-white hover:bg-[#082C5A]">
                    Choose a package
                  </Button>
                </Link>
              </div>
            )}
          </motion.div>

          {/* Conference brief */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="relative overflow-hidden rounded-3xl bg-apicon-navy p-6 text-white shadow-sm lg:col-span-7 lg:p-8"
            data-testid="dashboard-event-card"
          >
            <div className="pointer-events-none absolute inset-0 opacity-[0.08] heritage-pattern" />
            <div className="relative">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-gold">
                Your December
              </div>
              <div className="mt-3 font-display text-3xl font-bold leading-tight">
                Three days in Tinsukia — <br />
                see you there.
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/10">
                  <CalendarDays className="h-5 w-5 text-apicon-gold" />
                  <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/70">
                    Dates
                  </div>
                  <div className="mt-1 font-display text-lg font-semibold">
                    18 – 20 December 2026
                  </div>
                </div>
                <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/10">
                  <MapPin className="h-5 w-5 text-apicon-gold" />
                  <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/70">
                    Venue
                  </div>
                  <div className="mt-1 font-display text-lg font-semibold">
                    Tinsukia Medical College &amp; Hospital
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/committee" data-testid="dashboard-view-committee">
                  <Button
                    variant="outline"
                    className="rounded-full border-white/25 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                  >
                    Meet the committee
                    <ExternalLink className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </Link>
                <Link to="/" data-testid="dashboard-view-home">
                  <Button
                    variant="ghost"
                    className="rounded-full text-white hover:bg-white/10 hover:text-white"
                  >
                    Explore the programme
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Announcements */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-12 lg:p-8"
            data-testid="dashboard-announcements"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-apicon-gold" />
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                Announcements
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {[
                {
                  tag: "Programme",
                  title:
                    "Plenary lineup announced — seven perspectives on precision internal medicine.",
                },
                {
                  tag: "Travel",
                  title:
                    "Discounted delegate stays now open at three heritage hotels near Tinsukia.",
                },
                {
                  tag: "Workshops",
                  title:
                    "Pre-conference workshops open for pre-booking — 60 seats each, first come.",
                },
              ].map((a, i) => (
                <div
                  key={a.title}
                  className="rounded-2xl border border-gray-100 bg-apicon-mist/40 p-5"
                  data-testid={`dashboard-announcement-${i}`}
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-apicon-gold">
                    {a.tag}
                  </div>
                  <div className="mt-2 font-display text-base font-semibold leading-snug text-apicon-navy">
                    {a.title}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Abstract submissions — PG delegates only */}
          {user?.packageId === "pg" && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
            className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-12 lg:p-8"
          >
            <AbstractSubmissions />
          </motion.div>
          )}

          {/* Accompanying persons — Delegate (API / Non-API) only */}
          {(user?.packageId === "delegate-api" || user?.packageId === "delegate-nonapi") && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.28 }}
              className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-12 lg:p-8"
            >
              <AccompanyingPersons />
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
